import React, { useState, useEffect } from "react";
import { authStore } from "../lib/api";
import { FileSignature, Loader2, ExternalLink, X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function SignedContractCard({ leadId, zh, api }) {
  const token = authStore.get("ali_token");
  const fileUrl = (fid) => `${BACKEND}/api/files/${fid}/download?auth=${token}`;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [zoom, setZoom] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/crm/leads/${leadId}/signed-contract`)
      .then(({ data }) => { if (alive) setData(data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [leadId, api]);

  if (loading || !data || !data.signed || !data.thumbFileId) return null;

  const pages = (data.thumbFileIds && data.thumbFileIds.length ? data.thumbFileIds : [data.thumbFileId]).filter(Boolean);
  const openViewer = () => { setPage(0); setZoom(false); setOpen(true); };
  const prev = (e) => { e.stopPropagation(); setPage((p) => Math.max(0, p - 1)); setZoom(false); };
  const next = (e) => { e.stopPropagation(); setPage((p) => Math.min(pages.length - 1, p + 1)); setZoom(false); };

  return (
    <section data-testid="signed-contract-card">
      <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5"><FileSignature className="h-4 w-4 text-green-600" />{zh ? "客户签字版合同" : "Signed Contract"}</h3>
      <div className="flex items-center gap-4 rounded-xl border border-green-200 bg-green-50/40 p-3">
        <button data-testid="signed-contract-thumb" onClick={openViewer} className="shrink-0 rounded-lg overflow-hidden border border-slate-200 hover:border-green-400 transition-colors">
          <img src={fileUrl(data.thumbFileId)} alt="signed contract" className="h-32 w-24 object-cover object-top" />
        </button>
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-800">{zh ? "客户已签署的施工合同" : "Owner-signed construction contract"}</p>
          {data.contractNo && <p className="text-xs text-slate-500 mt-0.5">{zh ? "合同编号" : "Contract No."}: <span className="font-mono">{data.contractNo}</span></p>}
          <p className="text-[11px] text-slate-400 mt-1">{zh ? `${pages.length} 页 · 可翻页/放大快速核对` : `${pages.length} pages · flip & zoom`}</p>
          <button data-testid="signed-contract-open" onClick={openViewer} className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-green-700 border border-green-300 bg-white hover:bg-green-100 rounded-md px-2.5 py-1.5 transition-colors">
            <ExternalLink className="h-3.5 w-3.5" />{zh ? "查看完整合同" : "View full contract"}
          </button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex flex-col" data-testid="signed-contract-modal" onClick={() => setOpen(false)}>
          <div className="mobile-safe-topbar flex items-center justify-between px-4 py-2.5 text-white/90" onClick={(e) => e.stopPropagation()}>
            <span className="text-sm font-medium">{zh ? "客户签字版合同" : "Signed Contract"}{data.contractNo ? ` · ${data.contractNo}` : ""}</span>
            <div className="flex items-center gap-3">
              <span className="text-xs text-white/70" data-testid="signed-contract-page">{page + 1} / {pages.length}</span>
              <button data-testid="signed-contract-zoom" onClick={(e) => { e.stopPropagation(); setZoom((z) => !z); }} className="text-white/80 hover:text-white" title={zoom ? (zh ? "缩小" : "Zoom out") : (zh ? "放大" : "Zoom in")}>{zoom ? <ZoomOut className="h-5 w-5" /> : <ZoomIn className="h-5 w-5" />}</button>
              <a href={fileUrl(data.pdfFileId)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-white/80 hover:text-white" title={zh ? "打开 PDF" : "Open PDF"}><ExternalLink className="h-5 w-5" /></a>
              <button onClick={() => setOpen(false)} data-testid="signed-contract-close" className="text-white/80 hover:text-red-400"><X className="h-6 w-6" /></button>
            </div>
          </div>
          <div className="relative flex-1 overflow-auto flex items-start justify-center py-4" onClick={(e) => e.stopPropagation()}>
            <img data-testid="signed-contract-page-img" src={fileUrl(pages[page])} alt={`page ${page + 1}`}
              onClick={() => setZoom((z) => !z)}
              className={`rounded shadow-2xl bg-white transition-all cursor-zoom-in ${zoom ? "max-w-none w-[1100px] cursor-zoom-out" : "max-h-[82vh] w-auto"}`} />
            {page > 0 && <button data-testid="signed-contract-prev" onClick={prev} className="fixed left-3 top-1/2 -translate-y-1/2 text-white/80 hover:text-white bg-black/40 hover:bg-black/60 rounded-full p-2"><ChevronLeft className="h-6 w-6" /></button>}
            {page < pages.length - 1 && <button data-testid="signed-contract-next" onClick={next} className="fixed right-3 top-1/2 -translate-y-1/2 text-white/80 hover:text-white bg-black/40 hover:bg-black/60 rounded-full p-2"><ChevronRight className="h-6 w-6" /></button>}
          </div>
        </div>
      )}
    </section>
  );
}
